// Per-page widget visibility hook. Maintains the hidden-id list and the saved
// custom layout presets for the given page, backed by the reporting service's
// /dashboard/layout endpoints (per-tenancy, inherited up the parent chain then
// tenancy 0). Local state is optimistic so toggles are instant; changes are
// persisted with a debounced PUT. Exposes section objects with
// `visibleWidgets` / `anyVisible` so callers can suppress empty section titles.
//
// The GET returns the effective (possibly inherited) layout; the first edit
// saves this tenancy's own full set (adopt-then-edit), after which it no longer
// inherits. localStorage is no longer used — the table is the source of truth.
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { WIDGET_REGISTRY, WIDGET_SECTIONS } from './widgetRegistry';
import { FEATURES, subscribeFeatures, isWidgetEntitled } from './featureFlags';
import { useGetLayoutQuery, useSaveLayoutMutation } from 'store/services/reportLayout.service';

const SAVE_DEBOUNCE_MS = 600;

export default function useWidgetVisibility(page) {
    const rawTenancyId = useSelector((s) => s?.tenancy?.tenancyId);
    const tenancyId = Number(rawTenancyId);
    const hasTenancy = Number.isFinite(tenancyId) && tenancyId > 0;

    const allWidgets = WIDGET_REGISTRY[page] || [];
    const sectionDefs = WIDGET_SECTIONS[page] || [];

    // Effective layout for the whole tenancy (all pages); RTK Query dedupes the
    // three per-page hook instances down to a single request.
    const { data: layoutData, isError: layoutError } = useGetLayoutQuery(tenancyId, { skip: !hasTenancy });
    const [saveLayout] = useSaveLayoutMutation();

    const serverPage = layoutData?.pages?.[page];

    const [hidden, setHidden] = useState([]);
    const [templates, setTemplates] = useState([]);
    // Flips true once the layout has been seeded (or there's no tenancy / the
    // fetch errored). The shell waits on this so the first paint already has the
    // saved layout applied — no flash of the default.
    const [hydrated, setHydrated] = useState(false);

    // Refs hold the latest committed values so the debounced save and the
    // synchronous applyTemplate toggle-loop always read current state.
    const hiddenRef = useRef([]);
    const templatesRef = useRef([]);
    const saveTimer = useRef(null);
    // Re-seed when the layout first arrives, and again if the tenancy changes.
    const hydratedFor = useRef(null);

    useEffect(() => {
        // No tenancy yet: nothing to load, render defaults (all visible).
        if (!hasTenancy) { setHydrated(true); return; }
        if (hydratedFor.current === tenancyId) return;        // already seeded this tenancy
        // Wait for the GET unless it errored, in which case fall back to defaults.
        if (serverPage === undefined && !layoutError) return;
        hydratedFor.current = tenancyId;
        const h = Array.isArray(serverPage?.hidden) ? serverPage.hidden : [];
        const t = Array.isArray(serverPage?.templates) ? serverPage.templates : [];
        hiddenRef.current = h;
        templatesRef.current = t;
        setHidden(h);
        setTemplates(t);
        setHydrated(true);
    }, [hasTenancy, tenancyId, serverPage, layoutError]);

    // Re-render when feature flags change so vis() and section flags update.
    const [featureTick, bumpFeatures] = useState(0);
    useEffect(() => subscribeFeatures(() => bumpFeatures((n) => n + 1)), []);

    const scheduleSave = useCallback(() => {
        if (!hasTenancy) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveTimer.current = null;
            saveLayout({
                tenancyId,
                page,
                hidden: hiddenRef.current,
                templates: templatesRef.current,
            });
        }, SAVE_DEBOUNCE_MS);
    }, [hasTenancy, tenancyId, page, saveLayout]);

    // Commit any pending debounced save right now (called when the panel closes
    // and on unmount, so a change is never lost to a fast close/navigate). No-op
    // when nothing is pending, so it never fires a spurious write.
    const flushSave = useCallback(() => {
        if (!saveTimer.current) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        if (!hasTenancy) return;
        saveLayout({ tenancyId, page, hidden: hiddenRef.current, templates: templatesRef.current });
    }, [hasTenancy, tenancyId, page, saveLayout]);

    const flushRef = useRef(flushSave);
    flushRef.current = flushSave;
    useEffect(() => () => flushRef.current(), []);

    const commitHidden = useCallback((next) => {
        hiddenRef.current = next;
        setHidden(next);
        scheduleSave();
    }, [scheduleSave]);

    const commitTemplates = useCallback((next) => {
        templatesRef.current = next;
        setTemplates(next);
        scheduleSave();
    }, [scheduleSave]);

    const featureLookup = useMemo(() => {
        const m = new Map();
        allWidgets.forEach((w) => m.set(w.id, w.feature || null));
        return m;
    }, [allWidgets]);

    // Two independent gates. `feature` / entitlement are licence decisions set by
    // super admins on the Features page (stored on the admin-only features record);
    // `hidden` is the tenancy's own Customise choice. A widget the tenancy is not
    // entitled to is never rendered and never appears in Customise, so it cannot be
    // switched back on from inside the report.
    const widgetEnabled = useCallback((id) => {
        const flag = featureLookup.get(id);
        if (flag && !FEATURES[flag]) return false;
        return isWidgetEntitled(page, id);
    }, [featureLookup, page, featureTick]);

    const orderedGroup = useCallback((groupName) =>
        allWidgets.filter((w) => w.group === groupName),
    [allWidgets]);

    const dismiss = useCallback((id) => {
        if (hiddenRef.current.includes(id)) return;
        commitHidden([...hiddenRef.current, id]);
    }, [commitHidden]);

    const restore = useCallback((id) => {
        commitHidden(hiddenRef.current.filter((x) => x !== id));
    }, [commitHidden]);

    const restoreAll = useCallback(() => { commitHidden([]); }, [commitHidden]);

    const toggle = useCallback((id) => {
        const next = hiddenRef.current.includes(id)
            ? hiddenRef.current.filter((x) => x !== id)
            : [...hiddenRef.current, id];
        commitHidden(next);
    }, [commitHidden]);

    // Custom layout presets (saved against this tenancy's page record).
    const saveTemplate = useCallback((label) => {
        const name = (label || '').trim();
        if (!name) return;
        const entry = { id: `custom-${Date.now()}`, label: name, hidden: [...hiddenRef.current] };
        commitTemplates([...templatesRef.current, entry]);
    }, [commitTemplates]);

    const deleteTemplate = useCallback((id) => {
        commitTemplates(templatesRef.current.filter((t) => t.id !== id));
    }, [commitTemplates]);

    const vis = (id) => widgetEnabled(id) && !hidden.includes(id);

    const sections = useMemo(() =>
        sectionDefs.map((section) => {
            const visibleWidgets = section.widgets.filter((w) => widgetEnabled(w.id) && !hidden.includes(w.id));
            return {
                ...section,
                visibleWidgets,
                anyVisible: visibleWidgets.length > 0,
            };
        }),
    [sectionDefs, hidden, widgetEnabled]);

    return {
        hidden,
        allWidgets,
        orderedWidgets: allWidgets,
        orderedGroup,
        sections,
        vis,
        dismiss,
        restore,
        restoreAll,
        toggle,
        customTemplates: templates,
        saveTemplate,
        deleteTemplate,
        flushSave,
        // True once the layout has been applied (or there's no tenancy / it
        // errored). The shell gates page render on this to avoid a flash of the
        // default layout before the saved/inherited one loads.
        ready: hydrated,
    };
}
